import { NavigationBack, Typography } from "@/components";
import { cn } from "@/libraries/cn";
import { fromYearToAge } from "@/libraries/date";
import { MESSAGE_TAG } from "@/schemas";
import type { GetCaseResponse } from "@/types";
import { ChevronRightRounded } from "@mui/icons-material";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Toolbar from "@mui/material/Toolbar";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface AppBarCaseChatProps {
	caseItem: GetCaseResponse;
	tabs: { label: string; value: string; accent?: "green" }[];
	activeTab: string;
	activeSubTab?: string;
	onChangeTab: (value: string) => void;
	onClickSearch: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onChangeSubTab?: (
		event: React.MouseEvent<HTMLElement>,
		value: string | null,
	) => void;
}

const AppBarCaseChat = (props: AppBarCaseChatProps) => {
	const { t } = useTranslation("common");
	const {
		caseItem,
		tabs,
		activeTab = "all",
		activeSubTab = "all",
		onChangeTab,
		onClickSearch,
		onChangeSubTab,
	} = props;

	const navigate = useNavigate();

	const tabValue = tabs.findIndex((tab) => tab.value === activeTab);

	const title = useMemo(() => {
		if (!caseItem.patient) return caseItem.title;
		return [caseItem.patient.name, caseItem.patient.surname].join(" ");
	}, [caseItem]);

	const age = useMemo(() => {
		if (!caseItem.patient?.dob)
			return fromYearToAge(caseItem.year_of_birth, " yo");
		return fromYearToAge(caseItem.patient.dob, " yo");
	}, [caseItem]);

	const subtitle = useMemo(() => {
		if (!caseItem.patient) return caseItem.summary;
		return caseItem.title;
	}, [caseItem]);

	return (
		<>
			<AppBar
				position="relative"
				color="transparent"
				className="!bg-gradient-account [&_*_]:text-white"
			>
				<Toolbar className="md:!container !gap-4" sx={{ p: 0 }}>
					<NavigationBack />
					<Link
						to="/dashboard/case/view/$id/detail"
						params={{ id: caseItem.id }}
						className="flex flex-col justify-center items-start flex-1 min-w-0"
					>
						<Typography
							fontSize={14}
							fontWeight={700}
							className="truncate max-w-full flex items-center"
						>
							<span className="truncate">{title}</span>
							<ChevronRightRounded className="shrink-0" />
						</Typography>
						<Typography
							fontSize={12}
							fontWeight={400}
							className="flex justify-center items-center gap-2"
						>
							<Typography
								component="span"
								fontSize={12}
								fontWeight={400}
								className="flex items-end gap-1"
							>
								<PersonRoundedIcon fontSize="small" />
								{age}
							</Typography>
							<Typography
								component="span"
								fontSize={12}
								fontWeight={400}
								className="flex items-end gap-1"
							>
								<FolderRoundedIcon fontSize="small" />
								{subtitle}
							</Typography>
							{caseItem.patient?.ic_number ? (
								<span className="rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-medium leading-none text-white backdrop-blur-sm">
									IC {caseItem.patient.ic_number}
								</span>
							) : null}
						</Typography>
					</Link>
					<div>
						{caseItem.status !== "closed" ? (
							<IconButton
								color="primary"
								onClick={() =>
									navigate({
										to: "/dashboard/case/view/$id/members",
										params: { id: caseItem.id },
									})
								}
							>
								<PersonAddAltRoundedIcon className="text-default " />
							</IconButton>
						) : null}
						<IconButton
							color="primary"
							onClick={() =>
								navigate({
									to: "/dashboard/case/view/$id/detail",
									params: { id: caseItem.id },
									search: { tab: "medical_notes" },
								})
							}
						>
							<AssignmentRoundedIcon className="text-default " />
						</IconButton>
						<IconButton color="primary" onClick={onClickSearch}>
							<SearchOutlinedIcon className="text-default" />
						</IconButton>
					</div>
				</Toolbar>
				<div
					className="w-full overflow-x-auto no-scrollbar touch-pan-x flex items-end min-w-0"
					style={{
						WebkitOverflowScrolling: "touch",
					}}
				>
					<div className="flex flex-nowrap items-end shrink-0 min-w-full justify-start md:justify-center px-4">
						{tabs.map((tab) => {
							const isSelected = activeTab === tab.value;
							return (
								<button
									type="button"
									key={tab.value}
									onClick={() => onChangeTab(tab.value)}
									className={cn(
										"relative shrink-0 px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer select-none whitespace-nowrap",
										isSelected
											? "text-white font-bold"
											: "text-white/70 hover:text-white",
										tab.accent === "green" &&
											"!bg-emerald-500/40 !rounded-t-lg !font-bold",
									)}
								>
									{tab.label}
									{isSelected ? (
										<span className="absolute bottom-0 left-0 right-0 h-0.5 bg-white rounded-t-full" />
									) : null}
								</button>
							);
						})}
					</div>
				</div>
			</AppBar>
			{activeTab === MESSAGE_TAG.FILES && onChangeSubTab ? (
				<div
					className="w-full overflow-x-auto py-3 px-4 flex flex-row items-center justify-start md:justify-center touch-pan-x min-w-0"
					style={{
						WebkitOverflowScrolling: "touch",
						scrollbarWidth: "none",
						msOverflowStyle: "none",
					}}
				>
					<ToggleButtonGroup
						value={activeSubTab}
						exclusive
						aria-label="Team Scope"
						size="small"
						className="gap-2 flex-nowrap shrink-0 max-w-none"
						onChange={onChangeSubTab}
					>
						<ToggleButton
							key="all"
							value="all"
							aria-label="left aligned"
							className={cn(
								"!rounded-full !normal-case !text-zinc-950 !bg-white !border !border-zinc-300 !py-2 !px-3",
								"[&.Mui-selected]:!border-none [&.Mui-selected]:!bg-primary [&.Mui-selected]:!text-white",
							)}
						>
							<Typography fontWeight={500} fontSize={12}>
								{t("fileSubtabs.all")}
							</Typography>
						</ToggleButton>
						<ToggleButton
							key={MESSAGE_TAG.RESULTS}
							value={MESSAGE_TAG.RESULTS}
							aria-label="left aligned"
							className={cn(
								"!rounded-full !normal-case !text-zinc-950 !bg-white !border !border-zinc-300 !py-2 !px-3",
								"[&.Mui-selected]:!border-none [&.Mui-selected]:!bg-primary [&.Mui-selected]:!text-white",
							)}
						>
							<Typography fontWeight={500} fontSize={12}>
								{t("fileSubtabs.result")}
							</Typography>
						</ToggleButton>
						<ToggleButton
							key={MESSAGE_TAG.IMAGING}
							value={MESSAGE_TAG.IMAGING}
							aria-label="left aligned"
							className={cn(
								"!rounded-full !normal-case !text-zinc-950 !bg-white !border !border-zinc-300 !py-2 !px-3",
								"[&.Mui-selected]:!border-none [&.Mui-selected]:!bg-primary [&.Mui-selected]:!text-white",
							)}
						>
							<Typography fontWeight={500} fontSize={12}>
								{t("fileSubtabs.imaging")}
							</Typography>
						</ToggleButton>
						<ToggleButton
							key={MESSAGE_TAG.PHOTOS}
							value={MESSAGE_TAG.PHOTOS}
							aria-label="left aligned"
							className={cn(
								"!rounded-full !normal-case !text-zinc-950 !bg-white !border !border-zinc-300 !py-2 !px-3",
								"[&.Mui-selected]:!border-none [&.Mui-selected]:!bg-primary [&.Mui-selected]:!text-white",
							)}
						>
							<Typography fontWeight={500} fontSize={12}>
								{t("fileSubtabs.picture")}
							</Typography>
						</ToggleButton>
						<ToggleButton
							key={MESSAGE_TAG.ASKHEMI}
							value={MESSAGE_TAG.ASKHEMI}
							aria-label="askhemi"
							className={cn(
								"!rounded-full !normal-case !text-zinc-950 !bg-white !border !border-zinc-300 !py-2 !px-3",
								"[&.Mui-selected]:!border-none [&.Mui-selected]:!bg-violet-600 [&.Mui-selected]:!text-white",
							)}
						>
							<Typography fontWeight={500} fontSize={12}>
								AskHEMI
							</Typography>
						</ToggleButton>
						<ToggleButton
							key={MESSAGE_TAG.OTHER}
							value={MESSAGE_TAG.OTHER}
							aria-label="left aligned"
							className={cn(
								"!rounded-full !normal-case !text-zinc-950 !bg-white !border !border-zinc-300 !py-2 !px-3",
								"[&.Mui-selected]:!border-none [&.Mui-selected]:!bg-primary [&.Mui-selected]:!text-white",
							)}
						>
							<Typography fontWeight={500} fontSize={12}>
								{t("fileSubtabs.other")}
							</Typography>
						</ToggleButton>
					</ToggleButtonGroup>
				</div>
			) : null}
		</>
	);
};

export default AppBarCaseChat;
